import { useRef, useEffect } from "react";

// Standard one-time-code entry: auto-advance, backspace-back, full-code paste,
// numeric mobile keypad, and optional auto-submit on completion.
// Focus shifts are deferred to a useEffect (pendingFocus ref) so they apply AFTER
// React commits the controlled re-render — a synchronous .focus() inside onChange
// races the commit and gets dropped to <body>.
export function OtpInput({ value = "", onChange, onComplete, length = 6, dark = false, disabled = false }) {
  const refs = useRef([]);
  const pendingFocus = useRef(null);
  const digits = Array.from({ length }, (_, i) => value[i] || "");

  useEffect(() => {
    if (pendingFocus.current != null) {
      const el = refs.current[pendingFocus.current];
      if (el) { el.focus(); el.select?.(); }
      pendingFocus.current = null;
    }
  });

  const emit = (next) => {
    const clean = next.replace(/\D/g, "").slice(0, length);
    onChange(clean);
    if (clean.length === length) onComplete?.(clean);
    return clean;
  };

  const handleChange = (i, e) => {
    const raw = e.target.value.replace(/\D/g, "");
    if (!raw) return; // deletion handled in keydown
    const arr = value.split("");
    for (let k = 0; k < raw.length && i + k < length; k++) arr[i + k] = raw[k];
    emit(arr.join(""));
    pendingFocus.current = Math.min(i + raw.length, length - 1);
  };

  const handleKeyDown = (i, e) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      const arr = value.split("");
      if (digits[i]) {
        arr[i] = "";
        emit(arr.join(""));
        pendingFocus.current = i;
      } else if (i > 0) {
        arr[i - 1] = "";
        emit(arr.join(""));
        pendingFocus.current = i - 1;
      }
    } else if (e.key === "ArrowLeft" && i > 0) {
      refs.current[i - 1]?.focus();
    } else if (e.key === "ArrowRight" && i < length - 1) {
      refs.current[i + 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const text = (e.clipboardData.getData("text") || "").replace(/\D/g, "").slice(0, length);
    if (!text) return;
    emit(text);
    pendingFocus.current = Math.min(text.length, length - 1);
  };

  const boxCls = `rounded-lg w-11 h-12 text-2xl font-mono text-center border outline-none transition-all focus:ring-1 disabled:opacity-50 ${
    dark
      ? "bg-zinc-900 border-zinc-700 text-white focus:border-[#E0781A] focus:ring-[#E0781A]"
      : "bg-white border-slate-300 text-slate-900 focus:border-[#E0781A] focus:ring-[#E0781A]"
  }`;

  return (
    <div className="flex items-center gap-2">
      {digits.map((d, i) => (
        <input
          key={i}
          ref={(el) => (refs.current[i] = el)}
          data-testid={`totp-digit-${i}`}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          autoComplete="one-time-code"
          maxLength={1}
          disabled={disabled}
          value={d}
          onChange={(e) => handleChange(i, e)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onPaste={handlePaste}
          onFocus={(e) => e.target.select()}
          className={boxCls}
          aria-label={`Digit ${i + 1}`}
        />
      ))}
    </div>
  );
}
